import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { LayoutDashboard, CreditCard, Users, Shield, BarChart3, Settings, CheckCircle2, XCircle, Building2, Stethoscope, ShoppingBag, Heart, IndianRupee, Sparkles, Crown, Mail, MessageCircle, Video, FileText, Download } from "lucide-react";
import { Stat } from "./VetDashboard";

// Authenticated download — uses axios (cookies + bearer) and saves as a real
// binary blob. Plain `<a href>` was occasionally getting blocked by strict
// cookie policies and saving a 30-byte JSON error as `.pdf`/`.pptx`, which
// PowerPoint/Adobe then refused to open.
async function downloadInvestorDeck(fmt) {
  const tid = toast.loading(`Preparing ${fmt.toUpperCase()} download…`);
  try {
    const urlMap = {
      summary:  `${API}/admin/executive-summary`,
      proposal: `${API}/admin/investor-proposal`,
      pdf:      `${API}/admin/investor-deck`,
      pptx:     `${API}/admin/investor-deck`,
    };
    const url = urlMap[fmt];
    const params = (fmt === "pdf" || fmt === "pptx") ? { fmt } : {};
    const res = await axios.get(url, {
      params,
      responseType: "blob",
      withCredentials: true,
    });
    if (res.data && res.data.type && res.data.type.includes("application/json")) {
      const txt = await res.data.text();
      throw new Error(txt || "Download failed");
    }
    const mimeMap = {
      pdf: "application/pdf",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      summary: "application/pdf",
      proposal: "application/pdf",
    };
    const blob = new Blob([res.data], { type: mimeMap[fmt] });
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const nameMap = {
      pdf: `PetBond_Investor_Deck_${today}.pdf`,
      pptx: `PetBond_Investor_Deck_${today}.pptx`,
      summary: `PetBond_Executive_Summary_${today}.pdf`,
      proposal: `PetBond_Investor_Proposal_${today}.pdf`,
    };
    a.href = objectUrl;
    a.download = nameMap[fmt];
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
    const labelMap = { summary: "Summary", proposal: "Proposal", pdf: "PDF", pptx: "PPTX" };
    toast.success(`${labelMap[fmt] || fmt} downloaded`, { id: tid });
  } catch (e) {
    const msg = e?.response?.status === 401 || e?.response?.status === 403
      ? "Not signed in as admin. Please sign in again."
      : e?.response?.status === 404
      ? "File not generated yet on the server."
      : (e?.message || "Download failed");
    toast.error(msg, { id: tid });
  }
}

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "ai", label: "AI Settings", icon: Sparkles },
  { id: "notif", label: "Notifications", icon: Mail },
  { id: "subs", label: "Subscriptions", icon: Crown },
  { id: "users", label: "Users", icon: Users },
  { id: "kyc", label: "Vendor KYC", icon: Shield },
  { id: "revenue", label: "Revenue", icon: BarChart3 },
];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");

  if (user && user.role !== "super_admin") {
    return (
      <div className="bg-surface border border-line rounded-3xl p-8 text-center">
        <div className="font-heading text-xl font-bold">Admin only</div>
        <p className="text-ink-soft mt-1">This dashboard is restricted to PetBond team members.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <header className="rounded-3xl bg-gradient-to-br from-ink to-[#1a1a1a] text-white p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider font-bold opacity-70">PetBond Super Admin</div>
            <h1 className="font-heading text-2xl md:text-3xl font-extrabold">Welcome, {user?.name?.split(" ")[0]}</h1>
            <div className="opacity-80 text-sm mt-1">Manage the entire PetBond platform.</div>
          </div>
          {/* Investor Pitch Deck — prominent in the header so admins can't miss it */}
          <div className="flex flex-wrap items-center gap-2" data-testid="admin-deck-toolbar">
            <span className="text-[10px] font-bold tracking-widest uppercase opacity-60 mr-1">Investor Deck</span>
            <button
              type="button"
              onClick={() => downloadInvestorDeck("summary")}
              data-testid="download-exec-summary"
              className="rounded-full px-3.5 py-2 text-xs font-bold border inline-flex items-center gap-1.5 transition-colors bg-white/10 text-white border-white/20 hover:bg-white/20"
              title="Download 2-page executive summary (admin-only)"
            >
              <FileText className="w-3.5 h-3.5"/>Summary
            </button>
            <button
              type="button"
              onClick={() => downloadInvestorDeck("proposal")}
              data-testid="download-investor-proposal"
              className="rounded-full px-3.5 py-2 text-xs font-bold border inline-flex items-center gap-1.5 transition-colors bg-white/10 text-white border-white/20 hover:bg-white/20"
              title="Download full multi-page investor proposal (admin-only)"
            >
              <FileText className="w-3.5 h-3.5"/>Proposal
            </button>
            <button
              type="button"
              onClick={() => downloadInvestorDeck("pdf")}
              data-testid="download-deck-pdf"
              className="rounded-full px-3.5 py-2 text-xs font-bold border inline-flex items-center gap-1.5 transition-colors bg-brand text-white border-brand hover:bg-brand-hover"
              title="Download investor pitch deck (PDF, admin-only)"
            >
              <Download className="w-3.5 h-3.5"/>PDF
            </button>
            <button
              type="button"
              onClick={() => downloadInvestorDeck("pptx")}
              data-testid="download-deck-pptx"
              className="rounded-full px-3.5 py-2 text-xs font-bold border inline-flex items-center gap-1.5 transition-colors bg-white/10 text-white border-white/20 hover:bg-white/20"
              title="Download editable PowerPoint (admin-only)"
            >
              <FileText className="w-3.5 h-3.5"/>PPTX
            </button>
            <Link
              to="/app/admin/deck"
              data-testid="open-deck-viewer"
              className="rounded-full px-3.5 py-2 text-xs font-bold border inline-flex items-center gap-1.5 transition-colors bg-white/10 text-white border-white/20 hover:bg-white/20"
              title="View deck in browser"
            >
              <FileText className="w-3.5 h-3.5"/>View
            </Link>
          </div>
        </div>
      </header>

      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-bold border inline-flex items-center gap-2 transition-colors ${tab === t.id ? "bg-ink text-white border-ink" : "bg-surface border-line text-ink-soft hover:border-brand hover:text-ink"}`}
          >
            <t.icon className="w-4 h-4"/>{t.label}
          </button>
        ))}
        <Link
          to="/app/admin/videos"
          data-testid="admin-tab-videos"
          className="shrink-0 rounded-full px-4 py-2 text-sm font-bold border inline-flex items-center gap-2 transition-colors bg-brand text-white border-brand hover:bg-brand-hover"
        >
          <Video className="w-4 h-4"/>Promo Videos
        </Link>
        <Link
          to="/app/admin/insurance"
          data-testid="admin-tab-insurance"
          className="shrink-0 rounded-full px-4 py-2 text-sm font-bold border inline-flex items-center gap-2 transition-colors bg-surface border-line text-ink-soft hover:border-brand hover:text-ink"
        >
          <Shield className="w-4 h-4"/>Insurance
        </Link>
        <Link
          to="/app/admin/analytics"
          data-testid="admin-tab-analytics"
          className="shrink-0 rounded-full px-4 py-2 text-sm font-bold border inline-flex items-center gap-2 transition-colors bg-surface border-line text-ink-soft hover:border-brand hover:text-ink"
        >
          <FileText className="w-4 h-4"/>Analytics
        </Link>
        <Link
          to="/app/admin/pet-registrations"
          data-testid="admin-tab-pet-registrations"
          className="shrink-0 rounded-full px-4 py-2 text-sm font-bold border inline-flex items-center gap-2 transition-colors bg-surface border-line text-ink-soft hover:border-brand hover:text-ink"
        >
          <FileText className="w-4 h-4"/>Pet Licences
        </Link>
      </div>

      {tab === "overview" && <Overview/>}
      {tab === "payments" && <PaymentsTab/>}
      {tab === "ai" && <AISettingsTab/>}
      {tab === "notif" && <NotificationsTab/>}
      {tab === "subs" && <SubscriptionsTab/>}
      {tab === "users" && <UsersTab/>}
      {tab === "kyc" && <KycTab/>}
      {tab === "revenue" && <RevenueTab/>}
    </div>
  );
}

function NotificationsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [testTo, setTestTo] = useState({ email: "", whatsapp: "" });
  const [testing, setTesting] = useState("");

  const load = () => axios.get(`${API}/admin/notifications`).then(r => setS(r.data.settings));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const payload = { ...s };
      if (payload.resend_api_key && payload.resend_api_key.startsWith("***")) delete payload.resend_api_key;
      if (payload.twilio_auth_token && payload.twilio_auth_token.startsWith("***")) delete payload.twilio_auth_token;
      await axios.put(`${API}/admin/notifications`, payload);
      setMsg("Notification settings saved.");
      await load();
      setTimeout(() => setMsg(""), 3000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const test = async (channel) => {
    setTesting(channel); setMsg("");
    try {
      const r = await axios.post(`${API}/admin/notifications/test`, { channel, to: testTo[channel] });
      if (r.data.sent) setMsg(`Test ${channel} sent ✅`);
      else setMsg(`Test failed: ${r.data.skipped || r.data.error || "unknown"}`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(""); setTimeout(() => setMsg(""), 4500); }
  };

  if (!s) return <div className="text-ink-soft">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="notif-tab">
      {/* Resend */}
      <section className="bg-surface border border-line rounded-3xl p-6 shadow-soft space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-heading text-xl font-bold inline-flex items-center gap-2"><Mail className="w-5 h-5 text-brand"/> Resend — Transactional Email</h3>
          <Toggle label="Enabled" testid="resend-enabled" value={s.resend_enabled} onChange={v => setS({...s, resend_enabled: v})}/>
        </div>
        <p className="text-sm text-ink-soft">Get a free API key at <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-brand font-bold underline">resend.com/api-keys</a>. Add and verify your sending domain first.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <FieldI label="API Key"><input data-testid="resend-key" type="password" value={s.resend_api_key || ""} onChange={e=>setS({...s, resend_api_key:e.target.value})} className="li" placeholder="re_..."/></FieldI>
          <FieldI label="From email (verified)"><input data-testid="resend-from" value={s.resend_from_email || ""} onChange={e=>setS({...s, resend_from_email:e.target.value})} className="li" placeholder="PetBond &lt;noreply@updates.yourdomain.com&gt;"/></FieldI>
          <FieldI label="Reply-to (optional)"><input value={s.resend_reply_to || ""} onChange={e=>setS({...s, resend_reply_to:e.target.value})} className="li" placeholder="support@yourdomain.com"/></FieldI>
          <FieldI label="Send test to">
            <div className="flex gap-2">
              <input value={testTo.email} onChange={e=>setTestTo({...testTo, email:e.target.value})} className="li flex-1" placeholder="you@example.com"/>
              <button data-testid="test-email" onClick={() => test("email")} disabled={testing==="email" || !testTo.email} className="bg-ink text-white rounded-full px-3 py-2 text-xs font-bold disabled:opacity-50 whitespace-nowrap">{testing==="email" ? "…" : "Send"}</button>
            </div>
          </FieldI>
        </div>
      </section>

      {/* Twilio */}
      <section className="bg-surface border border-line rounded-3xl p-6 shadow-soft space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-heading text-xl font-bold inline-flex items-center gap-2"><MessageCircle className="w-5 h-5 text-brand-green"/> Twilio — WhatsApp / SMS</h3>
          <Toggle label="Enabled" testid="twilio-enabled" value={s.twilio_enabled} onChange={v => setS({...s, twilio_enabled: v})}/>
        </div>
        <p className="text-sm text-ink-soft">From <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="text-brand font-bold underline">Twilio Console</a>. For testing, use the sandbox WhatsApp number <code className="bg-surface-muted px-1.5 py-0.5 rounded">whatsapp:+14155238886</code> and join it from your phone first.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <FieldI label="Account SID"><input data-testid="twilio-sid" type="password" value={s.twilio_account_sid || ""} onChange={e=>setS({...s, twilio_account_sid:e.target.value})} className="li" placeholder="AC..."/></FieldI>
          <FieldI label="Auth Token"><input data-testid="twilio-token" type="password" value={s.twilio_auth_token || ""} onChange={e=>setS({...s, twilio_auth_token:e.target.value})} className="li" placeholder="your_auth_token"/></FieldI>
          <FieldI label="WhatsApp From"><input value={s.twilio_whatsapp_from || ""} onChange={e=>setS({...s, twilio_whatsapp_from:e.target.value})} className="li" placeholder="whatsapp:+14155238886"/></FieldI>
          <FieldI label="Send test to">
            <div className="flex gap-2">
              <input value={testTo.whatsapp} onChange={e=>setTestTo({...testTo, whatsapp:e.target.value})} className="li flex-1" placeholder="whatsapp:+91..."/>
              <button data-testid="test-whatsapp" onClick={() => test("whatsapp")} disabled={testing==="whatsapp" || !testTo.whatsapp} className="bg-ink text-white rounded-full px-3 py-2 text-xs font-bold disabled:opacity-50 whitespace-nowrap">{testing==="whatsapp" ? "…" : "Send"}</button>
            </div>
          </FieldI>
        </div>
      </section>

      {msg && <div className="text-sm font-bold text-leaf" data-testid="notif-msg">{msg}</div>}
      <button data-testid="notif-save" disabled={saving} onClick={save} className="bg-brand text-white rounded-full px-6 py-3 font-bold disabled:opacity-60 hover:bg-brand-hover">{saving ? "Saving…" : "Save notification settings"}</button>

      <section className="bg-surface border border-line rounded-3xl p-6 shadow-soft space-y-3" data-testid="vax-reminders-section">
        <h3 className="font-heading text-lg font-bold flex items-center gap-2">💉 Send vaccination reminders</h3>
        <p className="text-sm text-ink-soft">Manually trigger reminder email + WhatsApp for every pet whose next vaccination is due within the chosen window. Idempotent — owners only get one nudge per record per day.</p>
        <VaxRemindersAction/>
      </section>
      <style>{`.li{width:100%;border:1px solid #EAE0D5;border-radius:1.5rem;padding:0.5rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;font-size:14px;}.li:focus{border-color:#FF6B35;}`}</style>
    </div>
  );
}

function VaxRemindersAction() {
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const send = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await axios.post(`${API}/admin/send-vaccination-reminders?days_ahead=${days}`);
      setResult(r.data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Failed" });
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <label className="text-xs font-bold uppercase tracking-wider text-ink-soft">Window (days ahead):</label>
        <input type="number" min="1" max="30" value={days} onChange={e=>setDays(Number(e.target.value))} className="w-20 bg-surface-muted border border-line rounded-2xl px-3 py-1.5 font-bold text-sm" data-testid="vax-days"/>
        <button data-testid="vax-send" onClick={send} disabled={busy} className="bg-ink text-white rounded-full px-5 py-2 text-sm font-bold disabled:opacity-50 hover:bg-brand">{busy ? "Sending…" : "Send reminders now"}</button>
      </div>
      {result && (
        <div className="bg-surface-muted rounded-2xl p-3 text-sm" data-testid="vax-result">
          {result.error ? <span className="text-danger font-bold">{result.error}</span> : (
            <>Due in next {result.due_within_days}d: <strong>{result.due_count}</strong> · already sent today: {result.already_sent_today} · emails: <strong>{result.emails_sent}</strong> · WhatsApp: <strong>{result.whatsapps_sent}</strong></>
          )}
        </div>
      )}
    </div>
  );
}

function FieldI({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const MODEL_OPTIONS = {
  openai: ["gpt-5.4", "gpt-5.5", "gpt-5.4-mini", "gpt-5.2", "gpt-4o", "gpt-4.1"],
  anthropic: ["claude-sonnet-4-6", "claude-opus-4-7", "claude-sonnet-4-5-20250929", "claude-haiku-4-5-20251001", "claude-opus-4-5-20251101"],
  gemini: ["gemini-3.1-pro-preview", "gemini-3-flash-preview", "gemini-2.5-pro", "gemini-2.5-flash"],
};

function AISettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => { axios.get(`${API}/admin/ai-settings`).then(r => setS(r.data.settings)); }, []);

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const payload = { ...s };
      if (payload.api_key && payload.api_key.startsWith("***")) delete payload.api_key;
      await axios.put(`${API}/admin/ai-settings`, payload);
      setMsg("AI settings saved — Dr. PetBond now uses your config on the next message.");
      const r = await axios.get(`${API}/admin/ai-settings`);
      setS(r.data.settings);
      setTimeout(() => setMsg(""), 3500);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not save AI settings.");
    } finally { setSaving(false); }
  };

  if (!s) return <div className="text-ink-soft">Loading…</div>;
  const models = MODEL_OPTIONS[s.provider] || [];

  return (
    <div className="bg-surface border border-line rounded-3xl p-6 shadow-soft space-y-5" data-testid="ai-settings-tab">
      <div>
        <h3 className="font-heading text-xl font-bold inline-flex items-center gap-2"><Sparkles className="w-5 h-5 text-brand"/> Dr. PetBond AI</h3>
        <p className="text-sm text-ink-soft">Pick provider, model, system prompt and availability. Nothing is hardcoded — your config is the source of truth.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Toggle label="AI assistant enabled" testid="ai-enabled" value={s.enabled} onChange={v => setS({...s, enabled: v})}/>
        <Toggle label="Available 24 / 7" testid="ai-24-7" value={s.available_24_7} onChange={v => setS({...s, available_24_7: v})} hint="When off, available 8am–10pm IST only."/>
        <Toggle label="Premium plan required" testid="ai-premium-only" value={s.premium_only} onChange={v => setS({...s, premium_only: v})} hint="Limit chat to Premium subscribers."/>
        <Toggle label="Use my own API key" testid="ai-own-key" value={s.use_own_key} onChange={v => setS({...s, use_own_key: v})} hint="Off = use the built-in PetBond AI key (recommended)."/>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Provider</span>
          <select data-testid="ai-provider" value={s.provider} onChange={e => setS({...s, provider: e.target.value, model: MODEL_OPTIONS[e.target.value][0]})} className="mt-1.5 w-full bg-surface-muted border border-line rounded-2xl px-3 py-2 font-bold outline-none focus:border-brand">
            <option value="anthropic">Anthropic (Claude)</option>
            <option value="openai">OpenAI (GPT)</option>
            <option value="gemini">Google (Gemini)</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Model</span>
          <select data-testid="ai-model" value={s.model} onChange={e => setS({...s, model: e.target.value})} className="mt-1.5 w-full bg-surface-muted border border-line rounded-2xl px-3 py-2 font-bold outline-none focus:border-brand">
            {models.map(m => <option key={m} value={m}>{m}</option>)}
            {!models.includes(s.model) && <option value={s.model}>{s.model} (custom)</option>}
          </select>
        </label>
      </div>

      {s.use_own_key && (
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">API key (kept private)</span>
          <input data-testid="ai-api-key" type="password" value={s.api_key || ""} onChange={e => setS({...s, api_key: e.target.value})} placeholder="sk-…" className="mt-1.5 w-full bg-surface-muted border border-line rounded-2xl px-3 py-2 font-mono text-sm outline-none focus:border-brand"/>
        </label>
      )}

      <label className="block">
        <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">System prompt</span>
        <textarea data-testid="ai-system-prompt" rows={6} value={s.system_prompt || ""} onChange={e => setS({...s, system_prompt: e.target.value})} className="mt-1.5 w-full bg-surface-muted border border-line rounded-2xl px-3 py-2 text-sm outline-none focus:border-brand resize-none"/>
      </label>

      <label className="block max-w-xs">
        <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Max tokens per reply</span>
        <input data-testid="ai-max-tokens" type="number" min="100" max="4000" step="50" value={s.max_tokens} onChange={e => setS({...s, max_tokens: Number(e.target.value)})} className="mt-1.5 w-full bg-surface-muted border border-line rounded-2xl px-3 py-2 font-bold outline-none focus:border-brand"/>
      </label>

      {msg && <div className="text-sm font-bold text-leaf" data-testid="ai-save-msg">{msg}</div>}
      <button data-testid="ai-save" disabled={saving} onClick={save} className="bg-brand text-white rounded-full px-6 py-3 font-bold hover:bg-brand-hover disabled:opacity-60">{saving ? "Saving…" : "Save AI settings"}</button>
    </div>
  );
}

function Toggle({ label, value, onChange, hint, testid }) {
  return (
    <div className="bg-surface-muted rounded-2xl p-4 flex items-center justify-between gap-3">
      <div>
        <div className="font-bold text-sm">{label}</div>
        {hint && <div className="text-xs text-ink-soft mt-0.5">{hint}</div>}
      </div>
      <button data-testid={testid} type="button" onClick={() => onChange(!value)} className={`w-12 h-7 rounded-full p-0.5 transition-colors ${value ? "bg-brand" : "bg-line"}`}>
        <span className={`block w-6 h-6 bg-white rounded-full shadow-soft transition-transform ${value ? "translate-x-5" : ""}`}/>
      </button>
    </div>
  );
}

function SubscriptionsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/subscriptions`).then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-ink-soft">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="admin-subs-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Crown} label="Active" value={data.active} color="bg-brand/15 text-brand"/>
        <Stat icon={XCircle} label="Expired / cancelled" value={data.expired + (data.subscriptions.length - data.active - data.expired)} color="bg-line text-ink-soft"/>
        <Stat icon={Users} label="Total" value={data.subscriptions.length} color="bg-sky/15 text-sky"/>
        <Stat icon={IndianRupee} label="Revenue (active)" value={`₹${data.subscriptions.filter(s => s.status === "active").reduce((a, b) => a + (b.amount || 0), 0)}`} color="bg-leaf/30 text-ink"/>
      </div>
      <div className="bg-surface border border-line rounded-3xl shadow-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-muted text-left text-xs uppercase">
            <tr><th className="px-4 py-3">User</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Side</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Expires</th><th className="px-4 py-3">Amount</th></tr>
          </thead>
          <tbody>
            {data.subscriptions.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-soft">No subscriptions yet.</td></tr>}
            {data.subscriptions.map(s => (
              <tr key={s.subscription_id} className="border-t border-line" data-testid={`sub-row-${s.subscription_id}`}>
                <td className="px-4 py-3"><div className="font-bold">{s.user?.name}</div><div className="text-xs text-ink-soft">{s.user?.email}</div></td>
                <td className="px-4 py-3 font-bold">{s.plan_name}</td>
                <td className="px-4 py-3 capitalize">{s.side}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${s.status === "active" ? "bg-leaf/30 text-ink" : "bg-line text-ink-soft"}`}>{s.status}</span></td>
                <td className="px-4 py-3 text-xs text-ink-soft">{s.expires_at ? new Date(s.expires_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 font-bold">{s.amount ? `₹${s.amount}` : "Free"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/stats`).then(r => setStats(r.data)); }, []);
  if (!stats) return <div className="text-ink-soft">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Users} label="Users" value={stats.users} color="bg-brand/15 text-brand"/>
        <Stat icon={Stethoscope} label="Vets" value={stats.vets} color="bg-sky/15 text-sky"/>
        <Stat icon={ShoppingBag} label="Stores" value={stats.stores} color="bg-sunshine/30 text-ink"/>
        <Stat icon={Building2} label="Hostels" value={stats.hostels} color="bg-leaf/30 text-ink"/>
        <Stat icon={Heart} label="NGOs" value={stats.ngos} color="bg-brand/15 text-brand"/>
        <Stat icon={ShoppingBag} label="Orders" value={stats.orders} color="bg-sky/15 text-sky"/>
        <Stat icon={IndianRupee} label="GMV" value={`₹${stats.gmv}`} color="bg-leaf/30 text-ink"/>
        <Stat icon={Heart} label="Donations" value={`₹${stats.donations_total}`} color="bg-sunshine/30 text-ink"/>
      </div>
      <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        <h3 className="font-heading text-lg font-bold mb-3">Revenue split</h3>
        <div className="space-y-2 text-sm">
          <Row label="Store commission (15%)" value={`₹${stats.revenue_split.store_commission_15pct}`}/>
          <Row label="Vet commission (10%)" value={`₹${stats.revenue_split.vet_commission_10pct}`}/>
          <Row label="Donations passthrough (100%)" value={`₹${stats.revenue_split.donations_passthrough_100pct}`}/>
        </div>
      </div>
    </div>
  );
}

function PaymentsTab() {
  const [gateways, setGateways] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => axios.get(`${API}/admin/payment-config`).then(r => setGateways(r.data.gateways));
  useEffect(() => { load(); }, []);

  const save = async (g) => {
    setSaving(true); setMsg("");
    try {
      await axios.put(`${API}/admin/payment-config`, g);
      setMsg(`Saved ${g.gateway}. Live for: ${g.country_codes.join(", ") || "no countries"}.`);
      load();
      setTimeout(() => setMsg(""), 3000);
    } finally { setSaving(false); }
  };

  const liveGateways = gateways.filter(g => g.enabled);
  const liveCountries = [...new Set(liveGateways.flatMap(g => g.country_codes || []))];

  return (
    <div className="space-y-5" data-testid="admin-payments">
      <div className={`rounded-3xl p-5 border ${liveGateways.length > 0 ? "bg-leaf/20 border-leaf" : "bg-sunshine/20 border-sunshine"}`} data-testid="payments-status">
        <div className="flex items-start gap-3">
          <div className="text-2xl">{liveGateways.length > 0 ? "✅" : "⚠️"}</div>
          <div className="flex-1">
            <div className="font-heading text-lg font-bold">
              {liveGateways.length > 0 ? `Live in ${liveCountries.length} ${liveCountries.length === 1 ? "country" : "countries"}` : "No live payment gateway"}
            </div>
            <div className="text-sm text-ink-soft mt-1">
              {liveGateways.length > 0 ? (
                <>Enabled: <strong className="capitalize">{liveGateways.map(g => g.gateway).join(", ")}</strong> · Serving: <strong>{liveCountries.join(", ")}</strong>. The whole app uses these instantly — donations, store, vet & hostel bookings.</>
              ) : (
                <>The app is running in <strong>demo mode</strong>. Add Razorpay (India) or Stripe (global) keys below to go live.</>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-sunshine/20 border border-sunshine rounded-2xl p-4 text-sm">
        🔐 Keys are stored in your private database. They are never hardcoded. Use <strong>test mode</strong> keys until you're ready for live transactions.
      </div>
      {msg && <div className="text-sm font-bold text-leaf" data-testid="payments-save-msg">{msg}</div>}
      {gateways.map(g => (
        <GatewayCard key={g.gateway} gateway={g} onSave={save} saving={saving}/>
      ))}
    </div>
  );
}

function GatewayCard({ gateway, onSave, saving }) {
  const [g, setG] = useState(gateway);
  useEffect(() => setG(gateway), [gateway]);
  const isRz = g.gateway === "razorpay";
  return (
    <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid={`gateway-${g.gateway}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading text-xl font-bold capitalize">{g.gateway}</h3>
          <div className="text-xs text-ink-soft">Serves: {g.country_codes.join(", ")} · {g.currency}</div>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <span className="text-sm font-bold">Enabled</span>
          <input type="checkbox" data-testid={`${g.gateway}-enabled`} checked={!!g.enabled} onChange={e => setG({...g, enabled: e.target.checked})} className="w-5 h-5 accent-brand"/>
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <Field label={isRz ? "Key ID" : "Publishable Key"}>
          <input data-testid={`${g.gateway}-keyid`} value={g.key_id || ""} onChange={e => setG({...g, key_id: e.target.value})} className="ainput" placeholder={isRz ? "rzp_test_xxxx" : "pk_test_xxxx"}/>
        </Field>
        <Field label={isRz ? "Key Secret" : "Secret Key"}>
          <input data-testid={`${g.gateway}-secret`} type="password" value={g.key_secret?.startsWith("***") ? "" : (g.key_secret || "")} onChange={e => setG({...g, key_secret: e.target.value})} className="ainput" placeholder={g.key_secret?.startsWith("***") ? `Saved · ${g.key_secret}` : (isRz ? "Razorpay secret" : "sk_test_xxxx")}/>
        </Field>
        {!isRz && (
          <Field label="Publishable Key (Stripe pk_)">
            <input data-testid="stripe-publishable" value={g.publishable_key || ""} onChange={e => setG({...g, publishable_key: e.target.value})} className="ainput" placeholder="pk_test_..."/>
          </Field>
        )}
        <Field label="Currency">
          <input value={g.currency || ""} onChange={e => setG({...g, currency: e.target.value})} className="ainput"/>
        </Field>
        <Field label="Country codes (comma sep)">
          <input value={(g.country_codes || []).join(",")} onChange={e => setG({...g, country_codes: e.target.value.split(",").map(s=>s.trim().toUpperCase()).filter(Boolean)})} className="ainput"/>
        </Field>
        <Field label="Webhook secret (for verifying Razorpay/Stripe events)">
          <input data-testid={`${g.gateway}-webhook-secret`} type="password" value={g.webhook_secret?.startsWith("***") ? "" : (g.webhook_secret || "")} onChange={e => setG({...g, webhook_secret: e.target.value})} className="ainput" placeholder={g.webhook_secret?.startsWith("***") ? `Saved · ${g.webhook_secret}` : "whsec_…"}/>
        </Field>
      </div>
      {isRz && (
        <div className="mt-4 p-3 rounded-2xl bg-sky/10 border border-sky/40 text-xs">
          <div className="font-bold text-sky uppercase tracking-wider text-[10px] mb-1">Razorpay webhook URL</div>
          <code className="break-all font-mono text-[11px]">{`${window.location.origin.replace(/\/$/, "")}/api${"/razorpay/webhook"}`}</code>
          <p className="text-ink-soft mt-1">Configure this URL in <a href="https://dashboard.razorpay.com/app/webhooks" target="_blank" rel="noreferrer" className="text-sky underline font-bold">Razorpay Dashboard → Settings → Webhooks</a>. Select events: <code>subscription.activated</code>, <code>subscription.charged</code>, <code>subscription.halted</code>, <code>subscription.cancelled</code>, <code>subscription.completed</code>. Paste the webhook secret above.</p>
        </div>
      )}
      <button data-testid={`${g.gateway}-save`} disabled={saving} onClick={() => onSave(g)} className="mt-4 bg-brand text-white rounded-full px-5 py-2 font-bold hover:bg-brand-hover disabled:opacity-60">
        Save {g.gateway}
      </button>
      <style>{`.ainput{width:100%;border:1px solid #EAE0D5;border-radius:9999px;padding:0.6rem 1rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;color:#2C2C2C;outline:none;}.ainput:focus{border-color:#FF6B35;}`}</style>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  useEffect(() => { axios.get(`${API}/admin/users`).then(r => setUsers(r.data.users)); }, []);
  const suspend = async (uid, val) => {
    await axios.post(`${API}/admin/users/suspend`, { user_id: uid, suspended: val });
    axios.get(`${API}/admin/users`).then(r => setUsers(r.data.users));
  };
  return (
    <div className="bg-surface border border-line rounded-3xl shadow-soft overflow-hidden" data-testid="admin-users">
      <table className="w-full text-sm">
        <thead className="bg-surface-muted">
          <tr className="text-left">
            <th className="px-4 py-3 font-bold">Name</th>
            <th className="px-4 py-3 font-bold">Email</th>
            <th className="px-4 py-3 font-bold">Role</th>
            <th className="px-4 py-3 font-bold">Status</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.user_id} className="border-t border-line">
              <td className="px-4 py-3 font-bold">{u.name}</td>
              <td className="px-4 py-3 text-ink-soft">{u.email}</td>
              <td className="px-4 py-3"><span className="text-xs font-bold uppercase tracking-wider bg-surface-muted rounded-full px-2 py-1">{u.role || "parent"}</span></td>
              <td className="px-4 py-3">{u.suspended ? <span className="text-danger font-bold">Suspended</span> : <span className="text-leaf font-bold">Active</span>}</td>
              <td className="px-4 py-3 text-right">
                <button data-testid={`toggle-${u.user_id}`} onClick={() => suspend(u.user_id, !u.suspended)} className="text-xs font-bold text-brand hover:underline">
                  {u.suspended ? "Activate" : "Suspend"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KycTab() {
  const [data, setData] = useState({ vets: [], stores: [], hostels: [], ngos: [] });
  const load = () => axios.get(`${API}/admin/vendors`).then(r => setData(r.data));
  useEffect(() => { load(); }, []);
  const decide = async (type, id, approved) => {
    await axios.post(`${API}/admin/kyc`, { entity_type: type, entity_id: id, approved });
    load();
  };
  const Section = ({ title, list, type, idKey, labelKey="name", cityKey="city" }) => (
    <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
      <h3 className="font-heading text-lg font-bold mb-3">{title} ({list.length})</h3>
      <div className="space-y-2">
        {list.map(e => (
          <div key={e[idKey]} className="py-2 border-b border-line last:border-0">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-bold text-sm">{e[labelKey]}</div>
                <div className="text-xs text-ink-soft">{e[cityKey]} · KYC: <span className={`font-bold ${e.kyc_status === "approved" ? "text-leaf" : e.kyc_status === "rejected" ? "text-danger" : "text-sunshine"}`}>{e.kyc_status || "pending"}</span> · {e.kyc_documents?.length || 0} doc(s)</div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`admin-kyc-approve-${e[idKey]}`} onClick={() => decide(type, e[idKey], true)} className="p-1.5 rounded-full hover:bg-leaf/30" title="Approve"><CheckCircle2 className="w-4 h-4 text-leaf"/></button>
                <button data-testid={`admin-kyc-reject-${e[idKey]}`} onClick={() => decide(type, e[idKey], false)} className="p-1.5 rounded-full hover:bg-danger/10" title="Reject"><XCircle className="w-4 h-4 text-danger"/></button>
              </div>
            </div>
            {e.kyc_documents?.length > 0 ? (
              <div className="mt-3 p-2 bg-surface-muted rounded-xl">
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-soft mb-1.5">Uploaded documents ({e.kyc_documents.length})</div>
                <div className="flex flex-wrap gap-1.5">
                  {e.kyc_documents.map(d => (
                    <a
                      key={d.doc_id}
                      href={d.doc_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`kyc-doc-${d.doc_id}`}
                      className="text-xs font-bold bg-surface border border-line rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 hover:border-brand hover:bg-brand/5 transition-colors"
                      title={`${d.doc_name} · uploaded ${new Date(d.uploaded_at).toLocaleDateString()}`}
                    >
                      <FileText className="w-3.5 h-3.5 text-brand"/>
                      <span className="max-w-[160px] truncate">{d.doc_name}</span>
                      <span className="text-[9px] text-ink-soft">View →</span>
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-2 text-[11px] text-ink-soft italic" data-testid={`kyc-no-docs-${e[idKey]}`}>
                No documents uploaded yet — decision must be made based on off-platform verification.
              </div>
            )}
          </div>
        ))}
        {list.length === 0 && <div className="text-ink-soft text-sm">No entries.</div>}
      </div>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="admin-kyc">
      <Section title="Vets" list={data.vets} type="vet" idKey="vet_id"/>
      <Section title="Stores" list={data.stores} type="store" idKey="store_id"/>
      <Section title="Hostels" list={data.hostels} type="hostel" idKey="hostel_id"/>
      <Section title="NGOs" list={data.ngos} type="ngo" idKey="ngo_id"/>
    </div>
  );
}

function RevenueTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/stats`).then(r => setStats(r.data)); }, []);
  if (!stats) return <div className="text-ink-soft">Loading…</div>;
  return (
    <div className="bg-surface border border-line rounded-3xl p-6 shadow-soft" data-testid="admin-revenue">
      <h3 className="font-heading text-xl font-bold">Revenue snapshot</h3>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <Stat icon={IndianRupee} label="GMV (gross merchandise)" value={`₹${stats.gmv}`} color="bg-brand/15 text-brand"/>
        <Stat icon={IndianRupee} label="Donations passthrough" value={`₹${stats.donations_total}`} color="bg-leaf/30 text-ink"/>
        <Stat icon={IndianRupee} label="Store commission" value={`₹${stats.revenue_split.store_commission_15pct}`} color="bg-sunshine/30 text-ink"/>
        <Stat icon={IndianRupee} label="Vet commission" value={`₹${stats.revenue_split.vet_commission_10pct}`} color="bg-sky/15 text-sky"/>
      </div>
      <p className="text-xs text-ink-soft mt-4">Commissions are illustrative defaults (15% store / 10% vet). Configure final rates per partner contract.</p>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-line last:border-0">
    <span className="text-ink-soft">{label}</span>
    <span className="font-heading font-extrabold">{value}</span>
  </div>
);
